import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { NavLink, Navigate, Outlet, Route, Routes, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Users, Ticket, Megaphone, Star, Settings, ScrollText, LogOut, Menu, X, ShieldCheck, Activity, KeyRound, UserCog, Wallet, Contact, ShoppingCart } from 'lucide-react';
import { adminApi, apiError, ADMIN_TOKEN_KEY } from '../portal/api';
import { ErrorBox } from '../portal/ui';
import { NetixLogo, PortalFooter, WelcomeBanner, Marquee, ThemePicker, useColorTheme } from '../portal/brand';
import { AdminOverview, AdminCustomers, AdminTickets, AdminTicketDetail, AdminAnnouncements, AdminFeedback, AdminSettings, AdminAudit, AdminCredentials } from './pages';
import { AdminDirectory } from './directory';
import { AdminOrders } from './orders';
import { AdminSubUsers } from './subusers';
import { AdminBilling } from './billing';

const Ctx = createContext(null);
export const useAdmin = () => useContext(Ctx);

const NAV = [
  { to: '/Customer-dashboard', label: 'Overview', icon: LayoutDashboard, end: true },
  { to: '/Customer-dashboard/directory', label: 'Customers', icon: Contact, perm: 'customer_directory' },
  { to: '/Customer-dashboard/orders', label: 'Orders', icon: ShoppingCart, perm: 'manage_orders' },
  { to: '/Customer-dashboard/customers', label: 'Customers & Portal Access', icon: Users, perm: 'manage_customers' },
  { to: '/Customer-dashboard/credentials', label: 'Login Credentials', icon: KeyRound, perm: 'manage_customers' },
  { to: '/Customer-dashboard/tickets', label: 'Complaints & Tickets', icon: Ticket, perm: 'manage_tickets' },
  { to: '/Customer-dashboard/billing', label: 'Billing & Accounts', icon: Wallet, perm: 'manage_billing' },
  { to: '/Customer-dashboard/announcements', label: 'Service Announcements', icon: Megaphone, admin: true },
  { to: '/Customer-dashboard/feedback', label: 'Customer Feedback', icon: Star, admin: true },
  { to: '/Customer-dashboard/sub-users', label: 'Sub-users & Permissions', icon: UserCog, admin: true },
  { to: '/Customer-dashboard/settings', label: 'Portal Settings', icon: Settings, admin: true },
  { to: '/Customer-dashboard/audit', label: 'Audit Logs', icon: ScrollText, admin: true },
];
const canSee = (item, admin) => {
  if (!admin) return !item.perm && !item.admin;
  if (item.admin) return !!admin.is_super_admin;
  if (item.perm) return !!admin.is_super_admin || !!admin.permissions?.[item.perm];
  return true;
};

const AdminLogin = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', password: '' });
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);
  useEffect(() => { if (localStorage.getItem(ADMIN_TOKEN_KEY)) navigate('/Customer-dashboard'); }, [navigate]);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true); setErr('');
    try { const { data } = await adminApi.post('/api/portal-admin/auth/login', form); localStorage.setItem(ADMIN_TOKEN_KEY, data.access_token); navigate('/Customer-dashboard'); }
    catch (e2) { setErr(apiError(e2)); } finally { setLoading(false); }
  };
  const input = 'w-full rounded-xl border border-slate-300 px-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/40 focus:border-[#1E3A8A]';
  return (
    <div className="portal-root min-h-screen portal-auth-bg relative overflow-hidden flex items-center justify-center p-6" data-testid="admin-login-page">
      <div className="absolute inset-0 portal-auth-grid pointer-events-none" />
      <div className="relative w-full max-w-5xl grid lg:grid-cols-2 gap-8 items-center">
        <div className="hidden lg:block text-white">
          <NetixLogo className="h-14" testId="admin-auth-logo-desktop" />
          <p className="mt-10 inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/15 px-3 py-1 text-xs font-semibold tracking-wide text-blue-100"><ShieldCheck className="h-3.5 w-3.5 text-emerald-300" />Restricted · Authorised staff only</p>
          <h2 className="mt-5 text-4xl xl:text-5xl font-extrabold leading-tight">Customer Portal<br />Management</h2>
          <p className="mt-4 text-blue-100 max-w-md">Manage portal logins, complaints, announcements and customer visibility — connected live to Netix IP monitoring.</p>
          <div className="mt-10 grid grid-cols-3 gap-3 portal-float">
            {[{ i: KeyRound, t: 'Create customer logins' }, { i: Ticket, t: 'Resolve tickets' }, { i: Activity, t: 'Live monitoring data' }].map(({ i: I, t }) => (
              <div key={t} className="portal-glass rounded-2xl p-4 text-slate-900 shadow-2xl"><I className="h-5 w-5 text-[#C8102E]" /><p className="mt-2 text-xs font-bold">{t}</p></div>
            ))}
          </div>
        </div>
        <div className="w-full max-w-md mx-auto portal-auth-card bg-white rounded-3xl p-8 md:p-10">
          <div className="flex items-center justify-between mb-6">
            <NetixLogo className="h-9" chip={false} testId="admin-auth-logo-card" />
            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Portal Admin</span>
          </div>
          <p className="text-sm font-semibold text-[#C8102E]" data-testid="admin-welcome-message">Welcome back, Administrator</p>
          <h1 className="text-2xl font-extrabold text-slate-900 mt-1">Sign in to manage the customer portal</h1>
          <p className="text-sm text-slate-500 mt-1 mb-6">Use your Netix portal administration credentials.</p>
          <form onSubmit={submit} className="space-y-4">
            <input className={input} type="email" placeholder="Admin email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="admin-login-email" />
            <input className={input} type="password" placeholder="Password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="admin-login-password" />
            <ErrorBox message={err} testId="admin-login-error" />
            <button type="submit" disabled={loading} className="w-full rounded-xl bg-gradient-to-r from-blue-600 to-indigo-700 text-white font-bold py-3 shadow-lg shadow-blue-900/30 hover:shadow-xl transition-shadow disabled:opacity-60" data-testid="admin-login-submit">{loading ? 'Signing in…' : 'Sign in'}</button>
          </form>
          <p className="mt-6 text-[11px] text-slate-400 text-center">All actions are audit-logged · ISO/IEC 27001:2022</p>
        </div>
      </div>
    </div>
  );
};

const Pill = ({ dot, children, testId }) => <div className="flex items-center gap-2 bg-white/10 px-3 py-1 rounded-full text-sm" data-testid={testId}>{dot}<span>{children}</span></div>;

const ROLE_LABEL = { portal_admin: 'Portal Administrator', portal_subuser: 'Sub-user', portal_support: 'Support', portal_noc: 'NOC' };

const Guard = ({ item, children }) => {
  const { admin } = useAdmin();
  if (!admin) return null;
  if (canSee(item, admin)) return children;
  return (
    <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-800" data-testid="access-denied">
      <p className="font-bold">Access restricted</p><p>Your sub-user account does not have permission to open this section. Ask the portal administrator to grant it.</p>
      <NavLink to="/Customer-dashboard" className="inline-block mt-3 underline font-semibold">Back to overview</NavLink>
    </div>
  );
};
const guard = (path, el) => <Guard item={NAV.find((n) => n.to === `/Customer-dashboard/${path}`) || {}}>{el}</Guard>;

const AdminLayout = () => {
  const navigate = useNavigate();
  const [admin, setAdmin] = useState(null);
  const [open, setOpen] = useState(false);
  const [overview, setOverview] = useState(null);
  const [color, setColor] = useColorTheme();
  const refresh = useCallback(() => adminApi.get('/api/portal-admin/overview').then(({ data }) => setOverview(data)).catch(() => {}), []);
  useEffect(() => {
    if (!localStorage.getItem(ADMIN_TOKEN_KEY)) { navigate('/Customer-dashboard/login'); return; }
    adminApi.get('/api/portal-admin/auth/me').then(({ data }) => setAdmin(data.admin)).catch(() => {});
    refresh();
    const id = setInterval(refresh, 60000);
    return () => clearInterval(id);
  }, [navigate, refresh]);
  const logout = () => { localStorage.removeItem(ADMIN_TOKEN_KEY); navigate('/Customer-dashboard/login'); };
  const healthy = overview?.integration_health?.monitoring?.healthy;
  return (
    <Ctx.Provider value={{ admin, overview, refresh }}>
      <div className="portal-root min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex" data-theme={color} data-testid="admin-layout">
        {open && <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={() => setOpen(false)} />}
        <aside className={`portal-sidebar fixed lg:sticky top-0 z-50 h-screen w-72 text-white flex flex-col transition-transform ${open ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
          <div className="p-5 border-b border-white/10 flex items-center justify-between"><NetixLogo className="h-9" testId="admin-sidebar-logo" /><button className="lg:hidden" onClick={() => setOpen(false)}><X className="h-5 w-5" /></button></div>
          <div className="px-5 pt-4 pb-2 text-[11px] font-bold uppercase tracking-widest text-blue-200/60">Portal Administration</div>
          <nav className="flex-1 overflow-y-auto px-3 pb-3 space-y-1">{NAV.filter((n) => canSee(n, admin)).map(({ to, label, icon: Icon, end }) => <NavLink key={to} to={to} end={end} onClick={() => setOpen(false)} data-testid={`admin-nav-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`} className={({ isActive }) => `relative flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${isActive ? 'portal-nav-active text-white' : 'text-blue-100/80 hover:bg-white/10 hover:text-white'}`}><Icon className="h-4 w-4" />{label}</NavLink>)}</nav>
          <div className="m-3 rounded-xl bg-white/5 border border-white/10 p-3 text-xs text-blue-100/80"><p className="font-semibold text-white truncate" data-testid="admin-email">{admin?.email}</p><p>{ROLE_LABEL[admin?.role] || admin?.role}</p><button onClick={logout} className="mt-2 flex items-center gap-2 text-red-300 font-semibold hover:text-red-200" data-testid="admin-logout-btn"><LogOut className="h-4 w-4" />Logout</button></div>
        </aside>
        <div className="flex-1 min-w-0 flex flex-col">
          <header className="portal-topbar sticky top-0 z-30 bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
            <div className="px-4 md:px-8 h-16 flex items-center gap-3">
              <button className="lg:hidden p-2 rounded-lg hover:bg-white/15" onClick={() => setOpen(true)} data-testid="admin-menu-btn"><Menu className="h-5 w-5" /></button>
              <Activity className="h-5 w-5 hidden sm:block" />
              <h2 className="text-base md:text-xl font-bold truncate">Netix Customer Portal Management</h2>
              {overview && (
                <div className="hidden md:flex items-center gap-3 ml-4">
                  <Pill testId="admin-header-open-tickets" dot={<span className="w-2 h-2 bg-red-400 rounded-full" />}>{overview.tickets_open} Open tickets</Pill>
                  <Pill testId="admin-header-accounts" dot={<span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />}>{overview.active_accounts} Active logins</Pill>
                  <Pill testId="admin-header-monitoring" dot={<span className={`w-2 h-2 rounded-full ${healthy ? 'bg-green-400 animate-pulse' : 'bg-orange-400'}`} />}>Monitoring {healthy ? 'healthy' : 'stale'}</Pill>
                </div>
              )}
              <div className="ml-auto flex items-center gap-2">
                <ThemePicker theme={color} onChange={setColor} />
                <div className="hidden md:flex items-center gap-2 pl-2 border-l border-white/20">
                  <span className="h-9 w-9 rounded-full bg-white text-[#1E3A8A] text-sm font-black flex items-center justify-center shadow-md">{(admin?.name || 'A').slice(0, 1).toUpperCase()}</span>
                  <span className="text-sm font-semibold">{admin?.name || 'Admin'}</span>
                </div>
              </div>
            </div>
          </header>
          <main className="flex-1 p-4 md:p-8 max-w-7xl w-full mx-auto">
            <WelcomeBanner name={admin?.name || 'Admin'} role={ROLE_LABEL[admin?.role] || admin?.role} testId="admin-welcome-banner">
              <div className="bg-gradient-to-br from-green-400 via-emerald-500 to-green-600 rounded-lg px-3 py-1.5 shadow-lg text-white"><p className="text-xs font-semibold leading-tight">Portal accounts</p><p className="text-lg font-black leading-tight">{overview ? `${overview.active_accounts}/${overview.portal_accounts}` : '—'}</p></div>
              <div className="bg-gradient-to-br from-orange-500 to-red-500 rounded-lg px-3 py-1.5 shadow-lg text-white"><p className="text-xs font-semibold leading-tight">Open tickets</p><p className="text-lg font-black leading-tight">{overview?.tickets_open ?? '—'}</p></div>
              <div className="bg-gradient-to-br from-blue-500 via-indigo-600 to-purple-600 rounded-lg px-3 py-1.5 shadow-lg text-white"><p className="text-xs font-semibold leading-tight">Monitored</p><p className="text-lg font-black leading-tight">{overview?.customers ?? '—'}</p></div>
            </WelcomeBanner>
            <Marquee data={overview?.marquee} testId="admin-marquee" />
            <div className="portal-rise"><Outlet /></div>
          </main>
          <PortalFooter version="Customer Portal Management v1.0" testId="admin-footer" />
        </div>
      </div>
    </Ctx.Provider>
  );
};

export default function PortalAdminApp() {
  return (
    <Routes>
      <Route path="login" element={<AdminLogin />} />
      <Route element={<AdminLayout />}>
        <Route index element={<AdminOverview />} />
        <Route path="directory" element={guard('directory', <AdminDirectory />)} />
        <Route path="orders" element={guard('orders', <AdminOrders />)} />
        <Route path="orders/:orderId" element={guard('orders', <AdminOrders />)} />
        <Route path="customers" element={guard('customers', <AdminCustomers />)} />
        <Route path="credentials" element={guard('credentials', <AdminCredentials />)} />
        <Route path="sub-users" element={guard('sub-users', <AdminSubUsers />)} />
        <Route path="tickets" element={guard('tickets', <AdminTickets />)} />
        <Route path="billing" element={guard('billing', <AdminBilling />)} />
        <Route path="tickets/:ticketId" element={guard('tickets', <AdminTicketDetail />)} />
        <Route path="announcements" element={guard('announcements', <AdminAnnouncements />)} />
        <Route path="feedback" element={guard('feedback', <AdminFeedback />)} />
        <Route path="settings" element={guard('settings', <AdminSettings />)} />
        <Route path="audit" element={guard('audit', <AdminAudit />)} />
      </Route>
      <Route path="*" element={<Navigate to="/Customer-dashboard" replace />} />
    </Routes>
  );
}
